import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Text } from 'src/core/forms';
import { useFormApi } from 'src/core/forms';
import {
    MAX_QUANTITY_FOR_PRODUCTS,
    MAX_QUANTITY_FOR_PRODUCTS_WITHOUT_LIMIT,
} from 'src/components/ProductDetail/utils/sizeForm';
import { toNumber } from 'es-toolkit/compat';

import { Button } from '../Button/Button';

import classes from './Forms.scss';

type Props = {
    id: string;
    field: string;
    initialValue?: number | string;
    minValue?: number | null;
    maxValue?: number | null;
    isBundle?: boolean;
    useBigButtons?: boolean;
    isNewProductQtySelect?: boolean;
    isOptionPicked?: boolean;
    selectCorrectShippingMethods?: (sku: string, value: number) => void;
    simpleProductSku?: string | null;
};

export const QtySelect: React.FC<Props> = React.memo(
    ({
        field,
        id,
        isBundle,
        useBigButtons,
        initialValue,
        minValue,
        maxValue,
        isNewProductQtySelect,
        isOptionPicked,
        selectCorrectShippingMethods,
        simpleProductSku,
    }) => {
        const [maxValueReached, setMaxValueReached] = useState(false);
        const MAX_VALUE = maxValue
            ? maxValue >= 10000
                ? MAX_QUANTITY_FOR_PRODUCTS
                : maxValue
            : MAX_QUANTITY_FOR_PRODUCTS_WITHOUT_LIMIT;
        const MIN_VALUE = minValue ?? 0;

        const monitorMaxValue = (event) => {
            const value = event.target.value;

            if (toNumber(value) <= 0) selectCorrectShippingMethods?.(simpleProductSku!, 0);
            if (toNumber(value) > 0) selectCorrectShippingMethods?.(simpleProductSku!, toNumber(value));
            if (value < MAX_VALUE) setMaxValueReached(false);
            if (value >= MAX_VALUE) setMaxValueReached(true);
        };

        const api = useFormApi();

        const inc = useCallback(() => {
            const curr = api.getValue(field);
            selectCorrectShippingMethods?.(simpleProductSku!, toNumber(curr) + 1);
            if (isPositiveInteger(curr)) {
                const parsed = parseInt(curr);
                if (parsed < MAX_VALUE) {
                    api.setValue(field, String(parseInt(curr) + 1));
                    setMaxValueReached(false);
                }
                if (parsed === MAX_VALUE - 1) {
                    setMaxValueReached(true);
                }
            }
        }, [MAX_VALUE, api, field, selectCorrectShippingMethods, simpleProductSku]);
        const dec = useCallback(() => {
            const curr = api.getValue(field);
            if (toNumber(curr) > 0) {
                selectCorrectShippingMethods?.(simpleProductSku!, -1);
            }
            if (isPositiveInteger(curr)) {
                const parsed = parseInt(curr);
                if (parsed > MIN_VALUE) {
                    api.setValue(field, String(parsed - 1));
                }
                if (parsed <= MAX_VALUE) {
                    setMaxValueReached(false);
                }
            }
        }, [MAX_VALUE, MIN_VALUE, api, field, selectCorrectShippingMethods, simpleProductSku]);
        const onBlur = useCallback(() => {
            const v = api.getValue(field);
            if (toNumber(v) === 0) selectCorrectShippingMethods?.(simpleProductSku!, 0);
            if (v === undefined) {
                selectCorrectShippingMethods?.(simpleProductSku!, 0);
                api.setValue(field, '0');
                return;
            }
            if (isPositiveInteger(v)) {
                const parsed = parseInt(v);
                if (parsed > MAX_VALUE) {
                    selectCorrectShippingMethods?.(simpleProductSku!, MAX_VALUE);
                    api.setValue(field, String(MAX_VALUE));
                }
            }
        }, [MAX_VALUE, api, field, selectCorrectShippingMethods, simpleProductSku]);

        const onKeyUp = useCallback(
            (evt) => {
                switch (evt.code) {
                    case 'ArrowDown':
                        return dec();
                    case 'ArrowUp':
                        return inc();
                }
            },
            [dec, inc],
        );

        const mask = useMemo(() => intAndMax(maxValue ?? Infinity), [maxValue]);
        return (
            <div
                className={classnames(classes.qty, { [classes.newProductQty]: isNewProductQtySelect })}
                data-test-id={`QtySelect_${id}`}
            >
                {isBundle || isNewProductQtySelect ? (
                    <div
                        className={classnames({
                            [classes.productQtySelect]: isNewProductQtySelect && !isBundle,
                            [classes.bundleQtySelect]: isBundle && !isNewProductQtySelect,
                        })}
                    >
                        <Button
                            onClick={dec}
                            variant={isBundle ? 'light' : 'transparent'}
                            className={classnames(classes.qtyButton, {
                                [classes.productButton]: isNewProductQtySelect,
                                [classes.isBundle]: isBundle,
                                [classes.big]: useBigButtons,
                            })}
                        >
                            {isBundle && '-'}
                            {isNewProductQtySelect && !isBundle && (
                                <img
                                    className={classnames(classes.qtySelectMinus, {
                                        [classes.selectedQtySelectMinus]: isNewProductQtySelect && isOptionPicked,
                                    })}
                                    src="../../../static/icons/thin-minus.svg"
                                    alt=""
                                />
                            )}
                        </Button>
                        <Text
                            className={classnames({
                                [classes.qtyInput]: !isNewProductQtySelect,
                                [classes.isBundle]: isBundle,
                                [classes.productInput]: !isBundle && isNewProductQtySelect,
                                [classes.selectedQtyProductInput]: !isBundle && isOptionPicked && isNewProductQtySelect,
                            })}
                            id={id}
                            field={field}
                            type="text"
                            pattern="\d*"
                            mask={mask}
                            initialValue={String(initialValue ?? 0)}
                            autoComplete="off"
                            onBlur={onBlur}
                            onKeyUp={onKeyUp}
                            onChange={monitorMaxValue}
                            data-test-id="QtySelect_input"
                            maxLength={4}
                        />
                        <Button
                            onClick={inc}
                            variant={isBundle ? 'light' : 'transparent'}
                            className={classnames(classes.qtyButton, {
                                [classes.productButton]: isNewProductQtySelect,
                                [classes.isBundle]: isBundle,
                                [classes.big]: useBigButtons,
                            })}
                        >
                            {isBundle && '+'}
                            {isNewProductQtySelect && !isBundle && (
                                <img
                                    className={classnames(classes.qtySelectPlus, {
                                        [classes.qtySelectDisabled]: maxValueReached,
                                    })}
                                    src="../../../static/icons/thin-plus.svg"
                                    alt=""
                                />
                            )}
                        </Button>
                    </div>
                ) : (
                    <>
                        <Text
                            className={classnames(classes.qtyInput, { [classes.isBundle]: isBundle })}
                            id={id}
                            field={field}
                            type="text"
                            pattern="\d*"
                            mask={mask}
                            initialValue={String(initialValue ?? 0)}
                            autoComplete="off"
                            onBlur={onBlur}
                            onKeyUp={onKeyUp}
                            data-test-id="QtySelect_input"
                        />
                        <div className={classes.qtyButtons}>
                            <Button
                                onClick={dec}
                                variant={isBundle ? 'light' : 'transparent'}
                                className={classnames(classes.qtyButton, {
                                    [classes.isBundle]: isBundle,
                                    [classes.big]: useBigButtons,
                                })}
                            >
                                -
                            </Button>
                            <Button
                                onClick={inc}
                                variant={isBundle ? 'light' : 'transparent'}
                                className={classnames(classes.qtyButton, {
                                    [classes.isBundle]: isBundle,
                                    [classes.big]: useBigButtons,
                                })}
                            >
                                +
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    },
);

function intAndMax(max = Infinity) {
    return function (value) {
        const next = onlyIntInput(value);
        const asInt = parseInt(next);
        if (asInt > max) return String(max);
        return next;
    };
}
export function onlyIntInput(value) {
    const nums = isPositiveInteger(value);
    if (!nums) {
        return value.replace(/[^0-9]/g, '');
    }
    return value;
}
export function isPositiveInteger(n): boolean {
    return /^\d+$/.test(String(n));
}
export function isPositiveIntegerOverZero(n): boolean {
    if (isPositiveInteger(n)) {
        return parseInt(n) > 0;
    }
    return false;
}
export function isPositiveFloat(n): boolean {
    const parsed = parseFloat(n);
    if (!Number.isNaN(parsed) && parsed >= 0) {
        return true;
    }
    return false;
}
